<template>
  <div class="app-step-two-tabs-images">
    <app-modal
      v-model="showWarningMessage"
      :disabled="isLoading"
      confirm-text="OK"
      cancel-text=""
      @confirm="confirmOrder"
      @cancel="showWarningMessage = false"
    >
      <template #title> Warning </template>
      <p>
        You have not included any image with your press release. If you decide
        to add an image before submitting this order you can do so by revisiting
        this tab.
      </p>
    </app-modal>

    <div class="app-step-two-tabs__wrapper">
      <div class="app-step-two-tabs-images__body">
        <div class="app-step-two-tabs-images__line"></div>

        <h6 class="app-step-two-tabs-images__title">Image Upload</h6>
        <p class="app-step-two-tabs-images__descr">
          Free (not embedded) images. Upload up to 2 images for your press
          release - (.jpg, .jpeg, or .png allowed).
          <br />
          For embedding, check the Photo Embed box below. Image size: at least
          600 pixels on the longest side (800 pixels preferred).
        </p>
        <div class="app-step-two-tabs-images-row step-row">
          <div class="app-step-two-tabs-images-row__col step-col">
            <p class="app-step-two-tabs-images-row__title">
              Upload your first image
            </p>
          </div>

          <div class="app-step-two-tabs-images-row__col step-col">
            <app-file-selector
              v-model:file="state.image1"
              accept=".jpg, .png, .jpeg"
              refName="image1"
            ></app-file-selector>

            <div class="image-preview-container">
              <loading-bar v-if="isImageUploading"></loading-bar>
              <div v-else-if="uploadedImage1Url" class="image-preview">
                <img :src="uploadedImage1Url" alt="" />
                <delete-button
                  @onConfirm="handleDeleteImage('image1')"
                ></delete-button>
              </div>
            </div>
          </div>
        </div>
        <div class="app-step-two-tabs-upload-row step-row">
          <step-input
            v-model="state.image1Caption"
            label="Image 1 Caption"
            :validation-model="v$?.image1Caption"
          ></step-input>
        </div>
        <div class="app-step-two-tabs-images-row step-row">
          <div class="app-step-two-tabs-images-row__col step-col">
            <p class="app-step-two-tabs-images-row__title">
              Upload your second image
            </p>
          </div>
          <div class="app-step-two-tabs-images-row__col step-col">
            <app-file-selector
              v-model:file="state.image2"
              accept=".jpg, .png, .jpeg"
              refName="image2"
            ></app-file-selector>

            <div v-if="state.image2?.fileContent" class="image-preview">
              <img :src="state.image2.fileContent" alt="" />
              <delete-button
                @onConfirm="handleDeleteImage('image2')"
              ></delete-button>
            </div>
          </div>
        </div>

        <div class="app-step-two-tabs-upload-row step-row">
          <step-input
            v-model="state.image2Caption"
            label="Image 2 Caption"
            :validation-model="v$?.image2Caption"
          ></step-input>
        </div>

        <div class="app-step-two-tabs-images-footer">
          <div class="app-step-two-tabs-images-footer__line"></div>
          <app-tooltip class="upload-image-tooltip">
            <h5 class="app-step-two-tabs-images-footer__title">
              MultimediaWire™
            </h5>
            <a href="#" class="app-step-two-tabs-images-footer__link">
              <app-help-icon />
              <span
                >What's the difference between free and embedded images?</span
              >
            </a>
            <template #tooltip>
              <p>
                With the free image option, up to two images will be included on
                the PR Newswire press release website, prnewswire.com, but
                <b
                  >the images will not be included on any of the syndicated
                  sites</b
                >
                where your press release may be published.
              </p>
              <p>
                To include your images in your press release on most syndicated
                sites which publish it, you can
                <b>select the Photo Embed option</b>
                (additional charge).
              </p>
            </template>
          </app-tooltip>

          <div class="app-step-two-tabs-images-media">
            <app-checkbox
              v-model="state.embedImage"
              :disabled="disabledEmbedImage"
            >
              Photo Embed - $150.00: Have your image(s) embedded into your
              release and distributed by PR Newswire on as many as 2,000 media
              websites.
            </app-checkbox>

            <app-checkbox v-model="state.embedVideo">
              YouTube Embed - $200.00: Embed a YouTube video into your release
              and have it distributed by PR Newswire on as many as 700 media
              websites.
            </app-checkbox>

            <div v-if="state.embedVideo">
              <div class="app-step-two-tabs-upload-row step-row">
                <step-input
                  v-model="state.video"
                  label="YouTube URL"
                  :validation-model="v$?.video"
                ></step-input>
              </div>
              <div class="app-step-two-tabs-upload-row step-row">
                <step-input
                  v-model="state.videoCaption"
                  label="Caption"
                  :validation-model="v$?.videoCaption"
                ></step-input>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <navigation-buttons
      :disabled-outer-condition="isLoading"
      :loading="isLoading"
      @onPreviousButtonClick="handlePreviousClick"
      @onNextButtonClick="handleNextClick"
    ></navigation-buttons>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators'
import { useOrder } from '@core/composables/order/useOrder'
import { state, showWarningMessage } from './state'
import { useNavigationButtons } from '@modules/app-steps/step-two/navigation-buttons'
import { useTabsStore } from '@/stores/tabs'
import { isFile } from '@core/utils/file'

import AppModal from '@modules/ui/app-modal.vue'
import NavigationButtons from '@modules/app-steps/step-two/navigation-buttons/index.vue'
import AppTooltip from '@modules/ui/app-tooltip.vue'
import DeleteButton from '@modules/ui/delete-button.vue'
import StepInput from '@modules/app-steps/step-input.vue'
import AppFileSelector from '@modules/ui/app-file-selector.vue'
import AppHelpIcon from '@/assets/svg/help.svg?component'
import AppCheckbox from '@modules/ui/app-checkbox.vue'
import useVuelidate from '@vuelidate/core'
import LoadingBar from '@modules/ui/loading-bar.vue'

const VIDEO_EMBED_PRICE = 200

const { getFrontendTabBuilder } = useTabsStore()

const { navigate } = useNavigationButtons()
const { order, useUpdateOrder } = useOrder()
const { isLoading, updateOrder } = useUpdateOrder()

const isImageUploading = ref(false)

const isEmbedVideo = !!order?.value?.pressReleaseContentAttachments?.embedVideo
const disabledEmbedImage = computed(() => !state.image1 && !state.image2)
const uploadedImage1Url = computed(
  () => order?.value?.pressReleaseContentAttachments?.image1Url
)

if (!isEmbedVideo) {
  state.embedVideo = false
}

const isValidYoutubeUrl = helpers.regex(
  /^(https?:\/\/)?(www\.|m\.)?(youtube.com|youtu.be)\/([^\s]+)/
)
const isNotYoutubeShorts = helpers.regex(/^((?!\/shorts\/).)*$/)

const rules = computed(() => {
  const defaultRules = {
    image1Caption: {
      maxLength: maxLength(500),
    },
    image2Caption: {
      maxLength: maxLength(500),
    },
    video: {
      maxLength: maxLength(255),
      isYoutubeUrl: helpers.withMessage(
        'The provided link must be a valid YouTube URL.',
        isValidYoutubeUrl
      ),
      isYoutubeShorts: helpers.withMessage(
        'Sorry, YouTube shorts are not allowed in PR Newswire press releases.',
        isNotYoutubeShorts
      ),
    },
    videoCaption: {
      maxLength: maxLength(100),
      requiredIf: helpers.withMessage(
        'Caption is required for YouTube video.',
        requiredIf(state.video)
      ),
    },
  }

  if (state.image1) {
    defaultRules['image1Caption'].required = required
  }

  if (state.image2) {
    defaultRules['image2Caption'].required = required
  }

  return defaultRules
})

const v$ = useVuelidate(rules, state)

const confirmOrder = () => {
  const { image1Caption, image2Caption, embedVideo, video, videoCaption } =
    state

  const payload = {
    ...getFrontendTabBuilder(true),
    image1Caption,
    image2Caption,
  }

  if (embedVideo) {
    payload['embedVideo'] = embedVideo
    payload['video'] = video
    payload['videoCaption'] = videoCaption
  }

  updateOrder(
    payload,
    () => {
      showWarningMessage.value = false
      navigate(true)
    },
    () => (showWarningMessage.value = false)
  )
}

const handlePreviousClick = () =>
  updateOrder({ ...getFrontendTabBuilder() }, () => navigate())

const handleNextClick = async () => {
  const noErrors = await v$.value.$validate()
  if (!noErrors) return

  const { image1, image2 } = state
  image1 || image2 ? confirmOrder() : (showWarningMessage.value = true)
}

watch(
  () => state.image1,
  (image1) => {
    if (!isFile(image1)) return

    isImageUploading.value = true

    updateOrder(
      { image1 },
      () => (isImageUploading.value = false),
      () => (isImageUploading.value = false)
    )
  }
)

watch(
  () => state.image2,
  (image2) => {
    if (!isFile(image2)) return

    isImageUploading.value = true

    updateOrder(
      { image2 },
      () => (isImageUploading.value = false),
      () => (isImageUploading.value = false)
    )
  }
)

const handleDeleteImage = (image) => {
  updateOrder({ [image]: '' }, () => (state[image] = null))
}

const mockEmbedVideoPrice = () => {
  if (order.value) {
    order.value.total += VIDEO_EMBED_PRICE
    order.value.receipt.push({
      name: 'Embed Video: Yes',
      amount: VIDEO_EMBED_PRICE,
      mocked: true,
    })
  }
}

const unMockEmbedVideoPrice = () => {
  const mockedItemIndex = order.value.receipt.findIndex((item) => item.mocked)
  if (mockedItemIndex === -1) return

  order.value.total -= VIDEO_EMBED_PRICE
  order.value.receipt.splice(mockedItemIndex, 1)
}

watch([() => state.embedImage], (newValue) => {
  const [embedImage] = newValue
  updateOrder({ embedImage })
})

watch([() => state.embedVideo], (newValue) => {
  const [embedVideo] = newValue
  if (!embedVideo) {
    state.videoCaption = ''
    state.video = ''
  }

  !isEmbedVideo && embedVideo ? mockEmbedVideoPrice() : unMockEmbedVideoPrice()
  if (!isEmbedVideo) return

  updateOrder({ embedVideo })
})
</script>
